import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  type Filters,
  type TableColumn,
  type TableColumnSort,
  type TableFilter,
  assertType,
  isNullable,
  safeAbsurd,
} from '@ipfabric/utils';
import { isStringFromList } from '@ipfabric/utils/types/isStringFromList';
import { useRuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import {
  selectSimpleFilterOperators,
  textSimpleFilterOperators,
} from 'components/Table/components/SimpleFilter/helpers/filterOperators';
import { getChildFilterField } from 'components/Table/components/SimpleFilter/helpers/getChildFilterField';
import { getFilterFromReadable } from 'components/Table/components/SimpleFilter/helpers/getFilterFromReadable';
import type { ChangeFilterEvent } from 'components/Table/components/SimpleFilter/types/ChangeFilterEvent';
import type { SimpleFilterField } from 'components/Table/components/SimpleFilter/types/SimpleFilterField';
import { createSimpleFilterFieldsFromColumns } from 'components/Table/helpers/createSimpleFilterFieldsFromColumns';
import { updateSimpleFilterFields } from 'components/Table/helpers/updateSimpleFilterFields';
import { useMemo, useState } from 'react';
import { safeMatchRegexp } from 'utils/regexp/safeMatchRegexp';
import type { TableProps } from '../components/Table';

interface Props {
  columns: TableColumn<any>[];
  data: unknown[];
  initialFilters?: Filters;
  initialSort?: TableColumnSort;
}

type FrontendLocalTableHook = Pick<
  TableProps<unknown>,
  | 'data'
  | 'onChangeFilter'
  | 'onRemoveFilters'
  | 'onRestoreSettings'
  | 'onSortChange'
  | 'onSubmitFilters'
  | 'simpleFilterFields'
  | 'simpleFilterFieldsSubmitted'
  | 'sort'
>;

const supportedFrontendSimpleFilters: TableFilter['type'][] = [
  'select',
  'text',
];

const isSupportedFrontendSimpleFilter = (column: TableColumn<any>) =>
  column.filter?.type &&
  supportedFrontendSimpleFilters.includes(column.filter.type);

const compareSortValues = (a: unknown, b: unknown): number => {
  if (a === b) {
    return 0;
  }
  if (isNullable(a)) {
    return -1;
  }
  if (isNullable(b)) {
    return 1;
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return Number(a) - Number(b);
  }

  // `numeric: true` gives natural ordering for numeric-looking strings (e.g.
  // ids "2" < "11" instead of lexicographic "11" < "2").
  return String(a).localeCompare(String(b), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
};

const conformsToFilter = (row: any) => (filterField: SimpleFilterField) => {
  const realFilterField = getChildFilterField(filterField) ?? filterField;
  const parsedFilter = getFilterFromReadable(
    realFilterField.filter,
    realFilterField.value,
  );
  if (parsedFilter === null) {
    // unused field
    return true;
  }
  const [operator, value] = parsedFilter;

  switch (realFilterField.filter.type) {
    case 'text': {
      assertType(
        isStringFromList([
          ...textSimpleFilterOperators,
          // 'like' is the default operator
          'like',
        ] as const),
        operator,
      );
      switch (operator) {
        case 'eq':
          return row[realFilterField.column] === value;
        case 'neq':
          return row[realFilterField.column] !== value;
        case 'reg':
          return safeMatchRegexp(value as string, row[realFilterField.column]);
        case 'nreg':
          return !safeMatchRegexp(value as string, row[realFilterField.column]);
        case 'like':
          return (row[realFilterField.column] as string | null)
            ?.toLocaleLowerCase()
            .includes((value as string).toLocaleLowerCase());
        default:
          safeAbsurd(operator, `Unknown text operator ${operator}`);
          return true;
      }
    }
    case 'select':
      assertType(isStringFromList(selectSimpleFilterOperators), operator);
      switch (operator) {
        case 'eq':
          return row[realFilterField.column] === value;
        case 'neq':
          return row[realFilterField.column] !== value;
        default:
          safeAbsurd(operator, `Unknown select operator ${operator}`);
          return true;
      }
    default:
      stumble(
        `Unsupported frontend filter type ${realFilterField.filter.type}`,
      );
      return true;
  }
};

export const useFrontendLocalTable = (props: Props): FrontendLocalTableHook => {
  const ruleContext = useRuleContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: initial state is initialized once
  const initialFieldsState = useMemo(() => {
    const initialSimpleFilterFields = createSimpleFilterFieldsFromColumns(
      props.columns.filter(isSupportedFrontendSimpleFilter),
      props.initialFilters,
    );
    return {
      simpleFilterFields: initialSimpleFilterFields,
      simpleFilterFieldsSubmitted: initialSimpleFilterFields,
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: empty state is initialized once from the same columns
  const emptyFieldsState = useMemo(() => {
    const emptySimpleFilterFields = createSimpleFilterFieldsFromColumns(
      props.columns.filter(isSupportedFrontendSimpleFilter),
    );
    return {
      simpleFilterFields: emptySimpleFilterFields,
      simpleFilterFieldsSubmitted: emptySimpleFilterFields,
    };
  }, []);

  // Form state and submitted state must be in one state field,
  // otherwise when updating form state and immediately submitting in one tick,
  // the submitted state receives a stale value.
  const [{ simpleFilterFields, simpleFilterFieldsSubmitted }, setFieldsState] =
    useState(initialFieldsState);
  const [sort, setSort] = useState<TableColumnSort | undefined>(
    props.initialSort,
  );

  const data = useMemo(() => {
    const filteredData = props.data.filter((row) =>
      Object.values(simpleFilterFieldsSubmitted).every(conformsToFilter(row)),
    );
    if (!sort) {
      return filteredData;
    }

    const sortOrderMultiplier = sort.order === 'asc' ? 1 : -1;
    return [...filteredData].sort(
      (a, b) =>
        compareSortValues(
          (a as Record<string, unknown>)[sort.column],
          (b as Record<string, unknown>)[sort.column],
        ) * sortOrderMultiplier,
    );
  }, [props.data, simpleFilterFieldsSubmitted, sort]);

  const onChangeFilter = useStableCallback((event: ChangeFilterEvent) => {
    setFieldsState((s) => ({
      ...s,
      simpleFilterFields: updateSimpleFilterFields(
        s.simpleFilterFields,
        event,
        ruleContext,
      ),
    }));
  });

  const onSubmitFilters = useStableCallback(() => {
    setFieldsState((s) => ({
      ...s,
      simpleFilterFieldsSubmitted: s.simpleFilterFields,
    }));
  });

  const onRemoveFilters = useStableCallback(() => {
    setFieldsState(emptyFieldsState);
  });

  const onRestoreSettings = useStableCallback(() => {
    setFieldsState(initialFieldsState);
    setSort(props.initialSort);
  });

  const onSortChange = useStableCallback((nextSort: TableColumnSort) => {
    setSort(nextSort);
  });

  return {
    data,
    onChangeFilter,
    onRemoveFilters,
    onRestoreSettings,
    onSortChange,
    onSubmitFilters,
    simpleFilterFields,
    simpleFilterFieldsSubmitted,
    sort,
  };
};
